import { addMonths, endOfMonth, isAfter, isBefore, startOfMonth } from "date-fns";
import { useCallback, useState } from "react";

interface UseMonthNavigationProps {
  startDate: Date;
  endDate: Date;
  loadMore?: (date: Date) => void;
  maxMonths?: number;
}

interface UseMonthNavigationReturn {
  currentMonthIndex: number;
  allMonthDates: Date[];
  currentMonthDate: Date;
  isFirstMonth: boolean;
  isLastMonth: boolean;
  isMonthInLoadedRange: (monthDate: Date) => boolean;
  handlePreviousMonth: () => void;
  handleNextMonth: () => void;
  handleMonthChange: (newIndex: number) => void;
}

export function useMonthNavigation(props: UseMonthNavigationProps): UseMonthNavigationReturn {
  const { startDate, endDate, loadMore, maxMonths = 6 } = props;

  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);

  // Create all months upfront for smooth swiping
  const allMonthDates = Array.from({ length: maxMonths }, (_, index) => {
    const monthDate = addMonths(startDate, index);
    return startOfMonth(monthDate);
  });

  const isMonthInLoadedRange = useCallback(
    (monthDate: Date) => {
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      return !isAfter(monthStart, endDate) && !isBefore(monthEnd, startDate);
    },
    [startDate, endDate]
  );

  const navigateToMonth = useCallback(
    (newIndex: number) => {
      setCurrentMonthIndex(newIndex);

      const newMonthDate = allMonthDates[newIndex];
      if (!isMonthInLoadedRange(newMonthDate) && loadMore) {
        loadMore(newMonthDate);
      }
    },
    [allMonthDates, isMonthInLoadedRange, loadMore]
  );

  const handleMonthChange = useCallback(
    (newIndex: number) => {
      navigateToMonth(newIndex);
    },
    [navigateToMonth]
  );

  const handlePreviousMonth = useCallback(() => {
    if (currentMonthIndex > 0) {
      navigateToMonth(currentMonthIndex - 1);
    }
  }, [currentMonthIndex, navigateToMonth]);

  const handleNextMonth = useCallback(() => {
    if (currentMonthIndex < allMonthDates.length - 1) {
      navigateToMonth(currentMonthIndex + 1);
    }
  }, [currentMonthIndex, allMonthDates.length, navigateToMonth]);

  const currentMonthDate = allMonthDates[currentMonthIndex];
  const isFirstMonth = currentMonthIndex === 0;
  const isLastMonth = currentMonthIndex === allMonthDates.length - 1;

  return {
    currentMonthIndex,
    allMonthDates,
    currentMonthDate,
    isFirstMonth,
    isLastMonth,
    isMonthInLoadedRange,
    handlePreviousMonth,
    handleNextMonth,
    handleMonthChange,
  };
}
